import Cross from "@icons/cross.svg?react";
import GarageOpenSvg from "@icons/garage-open.svg?react";
import Plus from "@icons/plus.svg?url";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Plural, Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { Outlet } from "react-router-dom";

import { Button } from "@/common/components/button/button";
import { Failed, Info } from "@/common/components/info/info";
import { Metadata } from "@/common/components/metadata/metadata";
import { trackNewStorageClick } from "@/common/components/mixpanel/mixpanel-tracking";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spinner } from "@/common/components/spinner/spinner";
import { useCurrency } from "@/common/hooks/use-currency";
import { useRequiredErpCompany } from "@/common/hooks/use-erp-company";
import { currencyFormatter } from "@/common/services/formatter";
import { sum } from "@/common/utils/array";

import { FullscreenModalLayout } from "../layout/layout.fullscreen";
import { storagesQuery } from "./api";
import { CommodityCard } from "./components/commodity-card/commodity-card";
import { ThreeStepProgressBar } from "./components/progress-bar/progress-bar";
import { path as newStoragePath } from "./storages.storage";

const path = "management";

const Management = () => {
  const { id: companyId } = useRequiredErpCompany();
  const { _ } = useLingui();
  const [, currency] = useCurrency();
  // @ts-expect-error discrepancy between CF API and IDSign API
  const storages = useQuery(storagesQuery({ companyId, currency }));
  let jsx: React.ReactNode = null;

  if (storages.status === "error") {
    jsx = <Failed error={storages.error} />;
  }

  if (storages.status === "pending") {
    jsx = (
      <div className="flex basis-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (storages.status === "success") {
    if (!storages.data) {
      jsx = (
        <Info>
          <Trans>
            Nenašli jsme žádnou společnost, ve které můžete spravovat sklady.
          </Trans>
        </Info>
      );
    } else {
      jsx = (
        <>
          <ul className="mb-4 flex flex-col gap-y-6">
            {storages.data.map((storage) => {
              const amountSum = storage.offers
                .map((offer) => offer.amount)
                .reduce(sum, 0);
              const totalAmountSum = storage.offers
                .map((offer) => offer.totalAmount)
                .reduce(sum, 0);
              const totalOrderSum = storage.offers
                .map((offer) => offer.orderAmount)
                .reduce(sum, 0);
              const lastPriceSum = storage.offers
                .map((offer) => (offer.lastPrice ?? 0) * offer.totalAmount)
                .reduce(sum, 0);

              return (
                <li
                  className="flex flex-col rounded-2xl text-xs shadow-can-light-box"
                  key={storage.id}
                >
                  <SearchParamsLink
                    className="flex flex-col gap-y-2 p-4 text-can-slate-blue-gray"
                    to={storage.id}
                  >
                    <h3 className="flex items-center gap-x-2 text-base font-bold text-can-forest-teal [&_path]:fill-[currentColor]">
                      <GarageOpenSvg className="h-4" />
                      {storage.name}
                    </h3>
                    <p>
                      <Trans>Skladem</Trans>:{" "}
                      <Plural
                        _0="# tun"
                        few="# tuny"
                        many="# tun"
                        one="# tuna"
                        other="# tun"
                        value={amountSum}
                      />{" "}
                      | <Trans>Hodnota (nyní)</Trans>:{" "}
                      {currencyFormatter(currency).format(lastPriceSum)}
                    </p>
                    <ThreeStepProgressBar
                      values={{
                        freeAmount: amountSum,
                        orderAmount: totalOrderSum,
                        totalAmount: totalAmountSum,
                      }}
                    />
                    <CommodityCard.Available
                      available={amountSum}
                      order={totalOrderSum}
                      tons={totalAmountSum}
                    />
                  </SearchParamsLink>
                </li>
              );
            })}
          </ul>
          <Button.Container>
            <SearchParamsLink
              className="mt-auto rounded-2xl border border-can-forest-teal px-4 py-3 text-center text-can-forest-teal"
              onMouseDown={trackNewStorageClick}
              style={{
                // eslint-disable-next-line lingui/no-unlocalized-strings
                background: `url(${Plus}) left 16px center no-repeat`,
              }}
              to={newStoragePath}
            >
              <span>
                <Trans>Přidat sklad</Trans>
              </span>
            </SearchParamsLink>
          </Button.Container>
        </>
      );
    }
  }

  return (
    <>
      <FullscreenModalLayout
        headerButton={
          <SearchParamsLink replace to="..">
            <Cross />
          </SearchParamsLink>
        }
      >
        <Metadata title={_(msg`Spravovat sklady`)} />
        <FullscreenModalLayout.Title>
          <Trans>Spravovat sklady</Trans>
        </FullscreenModalLayout.Title>
        {jsx}
      </FullscreenModalLayout>
      <Outlet />
    </>
  );
};

export { Management, path };
